/** @jsxImportSource @emotion/react */

import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { toast } from 'react-toastify'
import { useAuthContext } from '../../context/auth.context'
import { CHIEF } from '../../constants'
import { roleNameToValue } from '../../utils'
import { getServerUri } from '../../utils/getServerUri'

const formatDate = (value) => {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('pl-PL')
  } catch (error) {
    return '—'
  }
}

const SettingsApiKeys = () => {
  const { userRole, token } = useAuthContext()
  const [keys, setKeys] = useState([])
  const [newKey, setNewKey] = useState('')
  const [loading, setLoading] = useState(false)
  const [revokingId, setRevokingId] = useState(null)

  const loadKeys = useCallback(() => {
    if (userRole < roleNameToValue(CHIEF) || !token) return
    fetch(`${getServerUri()}/api/v1/api-keys`, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Nie udało się pobrać kluczy API'))))
      .then((result) => setKeys(result.data || []))
      .catch((error) => toast.error(error.toString()))
  }, [token, userRole])

  useEffect(() => {
    loadKeys()
  }, [loadKeys])

  if (userRole < roleNameToValue(CHIEF)) return null

  const generate = () => {
    setLoading(true)
    fetch(`${getServerUri()}/api/v1/api-keys`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Nie udało się wygenerować klucza API'))))
      .then((result) => {
        setNewKey(result.key)
        setKeys((current) => [result.data, ...current])
      })
      .catch((error) => toast.error(error.toString()))
      .finally(() => setLoading(false))
  }

  const revoke = (keyId) => {
    if (!window.confirm('Usunąć ten klucz API? Integracje używające go utracą dostęp.')) return
    setRevokingId(keyId)
    fetch(`${getServerUri()}/api/v1/api-keys/${keyId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((response) => (response.ok ? response.json() : Promise.reject(new Error('Nie udało się usunąć klucza API'))))
      .then(() => {
        setKeys((current) => current.filter((key) => key._id !== keyId))
        toast.success('Klucz API został usunięty')
      })
      .catch((error) => toast.error(error.toString()))
      .finally(() => setRevokingId(null))
  }

  return (
    <Paper css={{ padding: '1.5rem', marginBottom: '2rem' }}>
      <Typography variant='h6'>Klucze API</Typography>
      <Typography variant='body2'>Generuj klucze do odczytu danych CITO przez zewnętrzne integracje.</Typography>
      <Button variant='contained' color='primary' onClick={generate} disabled={loading} css={{ margin: '1rem 0' }}>
        {loading ? 'Generowanie...' : 'Generuj klucz API'}
      </Button>
      {newKey && (
        <div css={{ marginBottom: '1.5rem' }}>
          <Typography variant='body2'>Skopiuj klucz teraz. Pełna wartość nie będzie pokazana ponownie.</Typography>
          <TextField fullWidth value={newKey} InputProps={{ readOnly: true }} variant='outlined' margin='dense' />
          <Button onClick={() => navigator.clipboard?.writeText(newKey)}>Kopiuj klucz</Button>
        </div>
      )}
      {keys.length === 0 ? (
        <Typography variant='body2' color='textSecondary'>
          Brak aktywnych kluczy.
        </Typography>
      ) : (
        <TableContainer>
          <Table size='small' aria-label='Lista kluczy API'>
            <TableHead>
              <TableRow>
                <TableCell>Klucz</TableCell>
                <TableCell>Zakres</TableCell>
                <TableCell>Utworzono</TableCell>
                <TableCell>Ostatnie użycie</TableCell>
                <TableCell align='right' />
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((key) => (
                <TableRow key={key._id}>
                  <TableCell css={{ fontFamily: 'monospace' }}>{key.prefix}…</TableCell>
                  <TableCell>{(key.scopes || []).join(', ')}</TableCell>
                  <TableCell>{formatDate(key.createdAt)}</TableCell>
                  <TableCell>{formatDate(key.lastUsedAt)}</TableCell>
                  <TableCell align='right'>
                    <IconButton
                      aria-label='Usuń klucz API'
                      size='small'
                      onClick={() => revoke(key._id)}
                      disabled={revokingId === key._id}
                    >
                      <DeleteIcon fontSize='small' />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

export default SettingsApiKeys
